// pdf-extract.ts — pull the text out of a credit-report PDF, in the browser.
//
// pdf.js needs DOM globals that the Cloudflare Workers runtime doesn't have, so
// extraction runs client-side and only the text is sent to the server. We run
// pdf.js on the MAIN THREAD (no web worker) to avoid Vite worker-bundling
// fragility — credit reports are a handful of pages, so main-thread is fine.
// pdf.js is large, so it's lazy-loaded on first use (only when a staff member
// actually uploads a PDF) — it never weighs down the main app bundle.
let _pdfjs: any = null;
async function loadPdfjs(): Promise<any> {
  if (_pdfjs) return _pdfjs;
  const mod = await import("pdfjs-dist");
  // Point pdf.js at its bundled worker (Vite resolves this URL to a hashed
  // asset). pdf.js v6 hangs without a real worker, so main-thread mode is out.
  (mod as any).GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
  ).href;
  _pdfjs = mod;
  return mod;
}

export interface PdfExtractResult {
  text: string;
  pages: number;
  /** true when the PDF has no text layer (likely a scan/photo) — the caller
   *  should tell the user to save a real PDF instead of a screenshot. */
  imageOnly: boolean;
}

export async function extractPdfText(file: File): Promise<PdfExtractResult> {
  const pdfjs = await loadPdfjs();
  const buf = new Uint8Array(await file.arrayBuffer());
  const doc = await (pdfjs as any).getDocument({
    data: buf,
    isEvalSupported: false,
    useSystemFonts: true,
  }).promise;

  const parts: string[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const line = content.items.map((it: any) => (typeof it.str === "string" ? it.str : "")).join(" ");
    if (line.trim()) parts.push(line);
  }
  const text = parts.join("\n").replace(/[ \t]+/g, " ").trim();
  return { text, pages: doc.numPages, imageOnly: text.length < 20 };
}

/** Read a File as a base64 string (for storing the raw PDF alongside the text). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",").pop() || "");
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}
